/**
 * 高性能事件管理器
 *
 * @description
 * 提供带自动清理机制的事件管理系统，防止内存泄漏。
 * 支持弱引用、优先级、一次性监听器等高级功能。
 *
 * @example
 * ```typescript
 * const eventManager = new EventManager({
 *   enableAutoCleanup: true,
 *   cleanupInterval: 30000, // 30秒清理一次
 *   maxListeners: 1000
 * });
 *
 * // 添加监听器
 * const listenerId = eventManager.on('playerDeath', (data) => {
 *   console.log('玩家死亡:', data);
 * });
 *
 * // 添加弱引用监听器（自动清理）
 * eventManager.onWeak('gameUpdate', callback, gameObject);
 *
 * // 触发事件
 * eventManager.emit('playerDeath', { playerId: 123 });
 *
 * // 移除监听器
 * eventManager.off('playerDeath', listenerId);
 * ```
 */
export class EventManager {
    /**
     * 创建事件管理器
     * @param config 配置选项
     */
    constructor(config = {}) {
        this._listeners = new Map();
        this._cleanupTimer = null;
        this._nextListenerId = 1;
        this._config = {
            enableAutoCleanup: config.enableAutoCleanup ?? true,
            cleanupInterval: config.cleanupInterval ?? 30000, // 30秒
            maxListeners: config.maxListeners ?? 1000,
            listenerExpirationTime: config.listenerExpirationTime ?? 300000, // 5分钟
            enablePerformanceMonitoring: config.enablePerformanceMonitoring ?? false
        };
        this._stats = {
            totalListeners: 0,
            activeListeners: 0,
            totalEvents: 0,
            averageEventTime: 0,
            lastCleanupTime: Date.now()
        };
        // 启动自动清理
        if (this._config.enableAutoCleanup) {
            this._startAutoCleanup();
        }
    }
    /**
     * 添加事件监听器
     * @param eventName 事件名称
     * @param callback 回调函数
     * @param options 监听器选项
     * @returns 监听器ID
     */
    on(eventName, callback, options = {}) {
        const listenerId = this._generateListenerId();
        const listener = {
            callback,
            id: listenerId,
            createdAt: Date.now(),
            lastCalledAt: 0,
            callCount: 0,
            once: options.once ?? false,
            priority: options.priority ?? 0,
            weak: options.weak ?? false,
            owner: options.owner || undefined
        };
        // 检查监听器数量限制
        if (this._stats.totalListeners >= this._config.maxListeners) {
            console.warn(`事件监听器数量已达到上限 ${this._config.maxListeners}`);
            this._performCleanup(); // 尝试清理
            if (this._stats.totalListeners >= this._config.maxListeners) {
                throw new Error('无法添加更多事件监听器，已达到上限');
            }
        }
        // 添加到监听器列表
        if (!this._listeners.has(eventName)) {
            this._listeners.set(eventName, []);
        }
        const listeners = this._listeners.get(eventName);
        listeners.push(listener);
        // 按优先级排序（高优先级在前）
        listeners.sort((a, b) => b.priority - a.priority);
        this._stats.totalListeners++;
        this._updateActiveListeners();
        return listenerId;
    }
    /**
     * 添加弱引用监听器（自动清理）
     * @param eventName 事件名称
     * @param callback 回调函数
     * @param owner 拥有者对象
     * @param options 其他选项
     * @returns 监听器ID
     */
    onWeak(eventName, callback, owner, options = {}) {
        return this.on(eventName, callback, {
            ...options,
            weak: true,
            owner
        });
    }
    /**
     * 添加一次性监听器
     * @param eventName 事件名称
     * @param callback 回调函数
     * @param options 其他选项
     * @returns 监听器ID
     */
    once(eventName, callback, options = {}) {
        return this.on(eventName, callback, {
            ...options,
            once: true
        });
    }
    /**
     * 移除事件监听器
     * @param eventName 事件名称
     * @param listenerId 监听器ID
     * @returns 是否成功移除
     */
    off(eventName, listenerId) {
        const listeners = this._listeners.get(eventName);
        if (!listeners) {
            return false;
        }
        const index = listeners.findIndex(listener => listener.id === listenerId);
        if (index === -1) {
            return false;
        }
        listeners.splice(index, 1);
        this._stats.totalListeners--;
        // 如果没有监听器了，删除事件
        if (listeners.length === 0) {
            this._listeners.delete(eventName);
        }
        this._updateActiveListeners();
        return true;
    }
    /**
     * 移除所有指定事件的监听器
     * @param eventName 事件名称
     * @returns 移除的监听器数量
     */
    offAll(eventName) {
        const listeners = this._listeners.get(eventName);
        if (!listeners) {
            return 0;
        }
        const count = listeners.length;
        this._listeners.delete(eventName);
        this._stats.totalListeners -= count;
        this._updateActiveListeners();
        return count;
    }
    /**
     * 移除拥有者的所有监听器
     * @param owner 拥有者对象
     * @returns 移除的监听器数量
     */
    offByOwner(owner) {
        let removedCount = 0;
        for (const [eventName, listeners] of this._listeners) {
            for (let i = listeners.length - 1; i >= 0; i--) {
                const listener = listeners[i];
                if (listener.owner === owner) {
                    listeners.splice(i, 1);
                    removedCount++;
                    this._stats.totalListeners--;
                }
            }
            // 如果没有监听器了，删除事件
            if (listeners.length === 0) {
                this._listeners.delete(eventName);
            }
        }
        this._updateActiveListeners();
        return removedCount;
    }
    /**
     * 触发事件
     * @param eventName 事件名称
     * @param data 事件数据
     * @returns 成功调用的监听器数量
     */
    emit(eventName, data) {
        const listeners = this._listeners.get(eventName);
        if (!listeners || listeners.length === 0) {
            return 0;
        }
        const startTime = this._config.enablePerformanceMonitoring ? performance.now() : 0;
        let successCount = 0;
        const toRemove = [];
        // 执行监听器
        for (let i = 0; i < listeners.length; i++) {
            const listener = listeners[i];
            // 检查弱引用是否还有效（简化版本）
            if (listener.weak && listener.owner) {
                // 在支持WeakRef的环境中，这里可以进行更复杂的检查
                // 目前简化处理，假设对象仍然有效
            }
            try {
                listener.callback(data);
                listener.lastCalledAt = Date.now();
                listener.callCount++;
                successCount++;
                // 如果是一次性监听器，标记为移除
                if (listener.once) {
                    toRemove.push(i);
                }
            }
            catch (error) {
                console.error(`事件监听器执行失败 (${eventName}):`, error);
            }
        }
        // 移除标记的监听器（从后往前移除，避免索引问题）
        for (let i = toRemove.length - 1; i >= 0; i--) {
            const index = toRemove[i];
            listeners.splice(index, 1);
            this._stats.totalListeners--;
        }
        // 如果没有监听器了，删除事件
        if (listeners.length === 0) {
            this._listeners.delete(eventName);
        }
        // 更新统计信息
        this._stats.totalEvents++;
        if (this._config.enablePerformanceMonitoring && startTime > 0) {
            const executionTime = performance.now() - startTime;
            this._updateAverageEventTime(executionTime);
        }
        this._updateActiveListeners();
        return successCount;
    }
    /**
     * 检查是否有指定事件的监听器
     * @param eventName 事件名称
     * @returns 是否有监听器
     */
    hasListeners(eventName) {
        const listeners = this._listeners.get(eventName);
        return listeners ? listeners.length > 0 : false;
    }
    /**
     * 获取指定事件的监听器数量
     * @param eventName 事件名称
     * @returns 监听器数量
     */
    getListenerCount(eventName) {
        const listeners = this._listeners.get(eventName);
        return listeners ? listeners.length : 0;
    }
    /**
     * 获取所有事件名称
     * @returns 事件名称数组
     */
    getEventNames() {
        return Array.from(this._listeners.keys());
    }
    /**
     * 执行清理操作
     * @param force 是否强制清理所有监听器
     * @returns 清理的监听器数量
     */
    cleanup(force = false) {
        return this._performCleanup(force);
    }
    /**
     * 启动自动清理
     */
    _startAutoCleanup() {
        if (this._cleanupTimer) {
            return;
        }
        this._cleanupTimer = window.setInterval(() => {
            this._performCleanup();
        }, this._config.cleanupInterval);
    }
    /**
     * 停止自动清理
     */
    _stopAutoCleanup() {
        if (this._cleanupTimer) {
            clearInterval(this._cleanupTimer);
            this._cleanupTimer = null;
        }
    }
    /**
     * 执行清理操作
     */
    _performCleanup(force = false) {
        const now = Date.now();
        let removedCount = 0;
        for (const [eventName, listeners] of this._listeners) {
            for (let i = listeners.length - 1; i >= 0; i--) {
                const listener = listeners[i];
                let shouldRemove = force;
                if (!shouldRemove) {
                    // 检查弱引用（简化版本）
                    if (listener.weak && listener.owner) {
                        // 在支持WeakRef的环境中，这里可以进行更复杂的检查
                        // 目前简化处理
                    }
                    // 检查过期时间
                    if (!shouldRemove && this._config.listenerExpirationTime > 0) {
                        const age = now - listener.createdAt;
                        const timeSinceLastCall = now - listener.lastCalledAt;
                        if (age > this._config.listenerExpirationTime &&
                            timeSinceLastCall > this._config.listenerExpirationTime) {
                            shouldRemove = true;
                        }
                    }
                }
                if (shouldRemove) {
                    listeners.splice(i, 1);
                    removedCount++;
                    this._stats.totalListeners--;
                }
            }
            // 如果没有监听器了，删除事件
            if (listeners.length === 0) {
                this._listeners.delete(eventName);
            }
        }
        this._stats.lastCleanupTime = now;
        this._updateActiveListeners();
        return removedCount;
    }
    /**
     * 生成监听器ID
     */
    _generateListenerId() {
        return `listener_${this._nextListenerId++}_${Date.now()}`;
    }
    /**
     * 更新活跃监听器数量
     */
    _updateActiveListeners() {
        this._stats.activeListeners = this._stats.totalListeners;
    }
    /**
     * 更新平均事件处理时间
     */
    _updateAverageEventTime(executionTime) {
        if (this._stats.totalEvents === 1) {
            this._stats.averageEventTime = executionTime;
        }
        else {
            this._stats.averageEventTime =
                (this._stats.averageEventTime * (this._stats.totalEvents - 1) + executionTime) / this._stats.totalEvents;
        }
    }
    /**
     * 获取统计信息
     */
    getStats() {
        return { ...this._stats };
    }
    /**
     * 重置统计信息
     */
    resetStats() {
        this._stats = {
            totalListeners: this._stats.totalListeners, // 保留当前监听器数量
            activeListeners: this._stats.activeListeners,
            totalEvents: 0,
            averageEventTime: 0,
            lastCleanupTime: Date.now()
        };
    }
    /**
     * 获取配置信息
     */
    getConfig() {
        return { ...this._config };
    }
    /**
     * 销毁事件管理器
     */
    destroy() {
        this._stopAutoCleanup();
        this._listeners.clear();
        this._stats.totalListeners = 0;
        this._stats.activeListeners = 0;
    }
}
//# sourceMappingURL=EventManager.js.map